import * as _ from 'lodash';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { useState } from 'react';
import { DisplayBanner, GetActiveBannersResponse } from '../../../typings/api';
import { useAsyncStatus, useOnMount } from '../../hooks';
import { get } from '../../redux/api';
import { createLocalStorageInstance } from '../../helpers/localStorage';
import { AsyncStatus } from '../../types';
import { parseContentsJSON } from '../generic/QuillEditor';

type BannerIds = Array<number>;

type RenderedDisplayBanner = DisplayBanner & { renderedBody: string };

const BANNERS_LS_KEY = '__FOSSA_BANNERS_DISMISSED';
const ls = createLocalStorageInstance<BannerIds>(BANNERS_LS_KEY);

type UseActiveBanners = {
  status: AsyncStatus;
  banners: RenderedDisplayBanner[];
  onDismissBanner: (bannerId: number) => void;
};

/**
 * Manages fetching all active banners, and managing dismissed banners.
 */
const useActiveBanners = (): UseActiveBanners => {
  const [banners, setBanners] = useState<RenderedDisplayBanner[]>([]);
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const dismissedBanners = ls.get();

  /**
   * Determine whether the given banner should be displayed (whether it hasn't been dismissed).
   *
   * @param {DisplayBanner} banner - Given banner to check for.
   */
  function shouldDisplayBanner({ id: bannerId }: DisplayBanner): boolean {
    if (Array.isArray(dismissedBanners)) {
      return !dismissedBanners.includes(bannerId);
    }
    return true;
  }

  /**
   * Mark banner as dismissed by adding it to the localStorage list of dismissed banners.
   *
   * @param {number} bannerId - Banner to mark as dismissed.
   */
  function onDismissBanner(bannerId: number): void {
    setBanners(prevBanners => prevBanners.filter(banner => banner.id !== bannerId));
    ls.set(
      Array.isArray(dismissedBanners)
        ? [...dismissedBanners, bannerId]
        : [bannerId],
    );
  }

  useOnMount(async () => {
    onPending();
    try {
      const { data: displayBanners } = await get<GetActiveBannersResponse>('/api/banners/active');
      setBanners(
        // Save the banners that haven't been dismissed. Sort banners by ID so that we consistently display them in
        // the same order.
        _.sortBy(displayBanners?.filter(shouldDisplayBanner) ?? [], 'id')
          .map(banner => ({
            ...banner,
            renderedBody: (new QuillDeltaToHtmlConverter(parseContentsJSON(banner.body), {})).convert(),
          })),
      );
      onSuccess();
    } catch (error) {
      onError(error);
    }
  });

  return {
    status,
    banners: banners.slice(0, 3), // Limit to first three.
    onDismissBanner,
  };
};

export default useActiveBanners;
